import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, CheckCircle, Smartphone, History, Lock } from 'lucide-react';
import { format } from 'date-fns';

const TIPOS_LINHA = ['Linha Nova', 'TT', 'Portabilidade', 'Migração'];

export const StatusLinhaBadge = ({ status }) => (
  status === 'Ativo'
    ? <Badge className="bg-emerald-600 text-white border-0" data-testid="badge-status-linha">● Ativo</Badge>
    : <Badge className="bg-red-600 text-white border-0" data-testid="badge-status-linha">● Não ativo</Badge>
);

const fmtData = (iso) => {
  if (!iso) return '-';
  try { return format(new Date(iso), 'dd/MM/yyyy HH:mm'); } catch { return iso; }
};

const Info = ({ label, value, testid }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-sm font-medium break-all" data-testid={testid}>{value || '-'}</p>
  </div>
);

export const LinhaMovelDialog = ({ linha, aberto, onFechar, podeEditar, onSalvar, salvando }) => {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (linha) {
      setForm({
        tipo_linha: linha.tipo_linha || 'Linha Nova',
        ddd: linha.ddd || '',
        numero_linha: linha.numero_linha || '',
        operadora: linha.operadora || '',
        iccid: linha.iccid || '',
        telefone: linha.telefone || '',
        codigo_autorizacao: linha.codigo_autorizacao || '',
        status_linha: linha.status_linha || 'Não ativo',
        observacao_bko: linha.observacao_bko || '',
      });
    }
  }, [linha]);

  if (!linha || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v && !salvando) onFechar(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-linha-movel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-purple-400" />
            Linha {linha.indice}/{linha.qtd_linhas} — Venda {linha.protocolo}
          </DialogTitle>
          <DialogDescription>
            {podeEditar ? 'Edite os dados da linha. Toda alteração fica registrada no histórico e o vendedor é avisado.' : 'Somente leitura: apenas BKO/Admin alteram os dados da linha.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg border border-purple-900/40 bg-purple-950/20 p-3">
          <Info label="Cliente / Razão Social" value={linha.cliente_nome} testid="dlg-cliente" />
          <Info label="CPF/CNPJ" value={linha.cliente_documento} testid="dlg-documento" />
          <Info label="Vendedor" value={linha.vendedor_nome} testid="dlg-vendedor" />
          <Info label="Data da venda" value={fmtData(linha.venda_criado_em)} testid="dlg-data-venda" />
          <Info label="Proposta / HP" value={linha.hp} testid="dlg-hp" />
          <Info label="Plano móvel" value={linha.plano_movel_nome} testid="dlg-plano" />
          <Info label="Qtd. linhas" value={String(linha.qtd_linhas)} testid="dlg-qtd" />
          <Info label="Status da venda" value={linha.venda_status} testid="dlg-status-venda" />
        </div>

        {podeEditar ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Tipo da linha</Label>
              <Select value={form.tipo_linha} onValueChange={(v) => set('tipo_linha', v)}>
                <SelectTrigger className="mt-1" data-testid="select-tipo-linha"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_LINHA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>DDD</Label>
              <Input className="mt-1" maxLength={2} value={form.ddd} onChange={(e) => set('ddd', e.target.value.replace(/\D/g, ''))} placeholder="11" data-testid="input-ddd" />
            </div>
            <div>
              <Label>Número da linha</Label>
              <Input className="mt-1" value={form.numero_linha} onChange={(e) => set('numero_linha', e.target.value)} placeholder="(11) 99999-9999" data-testid="input-numero-linha" />
            </div>
            <div>
              <Label>Operadora</Label>
              <Select value={form.operadora || 'nenhuma'} onValueChange={(v) => set('operadora', v === 'nenhuma' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="select-operadora-linha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nenhuma">-</SelectItem>
                  {['Claro', 'Vivo', 'TIM', 'Oi', 'Algar', 'Outra'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ICCID</Label>
              <Input className="mt-1 font-mono" value={form.iccid} onChange={(e) => set('iccid', e.target.value.replace(/\D/g, ''))} placeholder="8955..." maxLength={20} data-testid="input-iccid" />
            </div>
            <div>
              <Label>Telefone</Label>
              <Input className="mt-1" value={form.telefone} onChange={(e) => set('telefone', e.target.value)} placeholder="(11) 99999-9999" data-testid="input-telefone-linha" />
            </div>
            <div>
              <Label>Código de autorização</Label>
              <Input className="mt-1 font-mono" value={form.codigo_autorizacao} onChange={(e) => set('codigo_autorizacao', e.target.value)} data-testid="input-codigo-autorizacao" />
            </div>
            <div>
              <Label>Status da linha</Label>
              <Select value={form.status_linha} onValueChange={(v) => set('status_linha', v)}>
                <SelectTrigger className="mt-1" data-testid="select-status-linha"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ativo">🟢 Ativo</SelectItem>
                  <SelectItem value="Não ativo">🔴 Não ativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label>Observação BKO/Admin</Label>
              <Textarea className="mt-1" rows={3} value={form.observacao_bko} onChange={(e) => set('observacao_bko', e.target.value)} placeholder="Observações internas sobre a linha..." data-testid="textarea-observacao-bko" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-lg border p-3">
            <Info label="Tipo da linha" value={linha.tipo_linha} />
            <Info label="DDD" value={linha.ddd} />
            <Info label="Número da linha" value={linha.numero_linha} />
            <Info label="Operadora" value={linha.operadora} />
            <Info label="ICCID" value={linha.iccid} testid="dlg-iccid" />
            <Info label="Telefone" value={linha.telefone} testid="dlg-telefone" />
            <Info label="Código de autorização" value={linha.codigo_autorizacao} />
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Status da linha</p>
              <StatusLinhaBadge status={linha.status_linha} />
            </div>
            <div className="col-span-2 md:col-span-4">
              <Info label="Observação BKO/Admin" value={linha.observacao_bko} testid="dlg-observacao" />
            </div>
            <p className="col-span-2 md:col-span-4 text-xs text-gray-500 flex items-center gap-1"><Lock className="h-3 w-3" /> Campos bloqueados para o vendedor após o envio da venda.</p>
          </div>
        )}

        <div>
          <p className="text-sm font-semibold flex items-center gap-1 mb-2"><History className="h-4 w-4" /> Histórico da linha</p>
          {(linha.historico || []).length === 0 ? (
            <p className="text-xs text-gray-500">Nenhuma alteração registrada.</p>
          ) : (
            <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="historico-linha">
              {[...linha.historico].reverse().map((h, i) => (
                <div key={i} className="text-xs rounded border border-gray-700/40 p-2">
                  <span className="font-semibold">{h.usuario}</span> <span className="text-gray-500">({h.perfil}) • {fmtData(h.data_hora)}</span>
                  <p className="mt-0.5">{h.detalhes}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={salvando} data-testid="btn-fechar-linha">Fechar</Button>
          {podeEditar && (
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => onSalvar(form)} disabled={salvando} data-testid="btn-salvar-linha">
              {salvando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-2 h-4 w-4" />}
              Salvar linha
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinhaMovelDialog;
